
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";

import Messages from '../../constants/Messages';

import Header from '../../components/header/header';
import Loader from '../../components/loader/loader';
import Search from '../../components/search/search';
import ProfileCard from '../../components/profile-card/profile-card';
import CartHighlighter from '../../components/cart-highlighter/cart-highlighter';
import Item from '../../components/item/item';
import InlineMessageBlock from '../../components/inline-message-block/inline-message-block';

import ProductService from '../../services/product-service';
import CartService from '../../services/cart-service';
import { useAuth } from '../../hooks/useAuth';
import AppRoutes from '../../constants/AppRoutes';
import Modal from '../../components/modal/modal';

import LocationCard from '../../components/location-card/location-card';
import Dropdown from '../../containers/dropdown/dropdown';
import AuthenticationService from '../../services/authentication-service';
import Constants from '../../constants/Constants';

const Home = () => {
    const [items, setItems] = useState([]);
    const [locations, setLocations] = useState([]);
    const [cartCount, setCartCount] = useState(0);
    const [cartTotalValue, setCartTotalValue] = useState(0);
    const [error, setError] = useState();
    const [loader, setLoader] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedValues, setSelectedValues] = useState({});
    const [userLocation, setUserLocation] = useState(selectedValues.selectedLocation);
    const [isLocationModal, setIsLocationModal] = useState(false);
    const [userDetails, setUserDetails] = useState();
    const [eventDetails, setEventDetails] = useState();
    const [isEventTime, setIsEventTime] = useState(false);
    const [balanceValue, setBalanceValue] = useState(0);
    const [userCurrentLocation, setUserCurrentLocation] = useState();

    const navigate = useNavigate();
    const { authLogout } = useAuth();
    let locationModalRef = useRef();

    useEffect(()=>{
        (async () => {
            await getItems();
            const userDetails = await AuthenticationService.getUserDetails();
            setUserDetails(userDetails);
            let eventDetails = await AuthenticationService.getEventDetails();
            let userSelectedLocation = eventDetails ? eventDetails?.user?.selectedLocation : undefined;
            setUserCurrentLocation(userSelectedLocation)
            let eventData = AuthenticationService.getEventData();
            setEventDetails(eventData);
            let balanceDetails = await AuthenticationService.getUserBalance();
            if(balanceDetails && balanceDetails.event) {
                setIsEventTime(true);
                setShowModal(true);
                if(eventDetails) {
                    setShowModal(false);
                    setUserLocation(eventDetails.user.selectedLocation);
                }  
                await getLocations();
            } else if(!balanceDetails.event && eventDetails){
                setIsEventTime(false);
                setShowModal(true);
            }
        })();
    }, [selectedValues]);

    useEffect(() => {
        let handler = (e) => {
            if(isLocationModal && !locationModalRef.current.contains(e.target)) {
                setSelectedValues({selectedLocation: selectedValues.selectedLocation});
                setIsLocationModal(false);
            }
        }
        document.addEventListener(Constants.HANDLER.MOUSEDOWN, handler);

        return () => {
            document.removeEventListener(Constants.HANDLER.MOUSEDOWN, handler);
        }

    }, [isLocationModal]);

    /** ------------------------------------------------------------------
     * Method : getItems
     * Description : get all products and show it to user
     */
    const getItems = async () => {
        try{
            let locationId = Constants.SHOP.DEFAULT_LOCATION.ID;
            const selectedLocationDetails = await AuthenticationService.getEventDetails();
            const userDetails = await AuthenticationService.getUserDetails();
            if(selectedLocationDetails) {
                let locationsDetails = await AuthenticationService.getLocationDetails();
                let selectedLocation = selectedLocationDetails?.user?.selectedLocation;
                for(let location of locationsDetails) if(location.location_name === selectedLocation) {
                    locationId = location.location_id;
                    break;
                }
            } else {
                locationId = userDetails ? userDetails?.locationDetails?.id : locationId;
            }
            setLoader(true);
            const items = await ProductService.getItems(locationId);
            let balanceDetails = await AuthenticationService.getUserBalance();
            setBalanceValue(balanceDetails?.event?.amount);
            setItems(items);
            getCart();
            setError(null);
        } catch (error) {
            if(error.code === Messages.ERROR_CODES.INVALID_JWT.code) {
                await authLogout();
                navigate(AppRoutes.LOGIN);
            } else {
                // If there is an error in getting the items, clear the existing items in the cart
                await CartService.clearCart();     
                setError(error);
            }
        }
        setLoader(false);
    };

    /** ------------------------------------------------------------------
     * Method : getLocations
     * Description : get all active locations and show it to user
     */
    const getLocations = async () => {
        try{
            setLoader(true);
            let locationDetails = AuthenticationService.getLocationDetails();
            if(!locationDetails) {
                const locations = await ProductService.getLocations();
                setLocations(locations);
                AuthenticationService.setLocationDetails(locations);
                setError(null);
            } else {
                setLocations(locationDetails);
            }
        } catch (error) {
            if(error.code === Messages.ERROR_CODES.INVALID_JWT.code) {
                await authLogout();
                navigate(AppRoutes.LOGIN);
            } else {
                // If there is an error in getting the items, clear the existing items in the cart
                await CartService.clearCart();     
                setError(error);
            }
        }
        setLoader(false);
    };

    /** ------------------------------------------------------------------
     * Method : onSearchText
     * Description : search items
     * @param {*} e 
     */
    const onSearchText = async (e) => {
        const searchResults = await ProductService.searchItems(e.target.value);
        setItems(searchResults);
    }

    /** ------------------------------------------------------------------
     * Method : updateCart
     * Description : Update the items in the cart
     * @param {*} item 
     * @param {*} qty 
     */
    const updateCart = async (item, qty) => {
        await CartService.updateCart(item, qty);
        await getCart();
    }

    /** ------------------------------------------------------------------
     * Method : getCart
     * Description : Get the cart count and value
     * @param {*} item 
     * @param {*} qty 
     */
    const getCart = async() => {
        const { totalCartQty, totalCartValue } = CartService.getCart();
        setCartCount(totalCartQty);
        setCartTotalValue(totalCartValue);
    }

    const handleOnClose = () => setShowModal(false);

    showModal ? document.body.style.overflow = Constants.STYLES.HIDDEN : document.body.style.overflow = Constants.STYLES.AUTO;

    return (
        <div className="bg-gray-100">
          <Header />
          <div className="max-w-lg mx-auto min-h-screen pt-4">
            <ProfileCard
              balanceValue={balanceValue}
              userCurrentLocation={userCurrentLocation}
              isLoading={loader}
            />
            <section className="pb-20 relative">
              <div className="container p-4 pt-0 mx-auto">
                {isEventTime ? (
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="pr-4 font-bold">
                      {Messages.SHOP.CURRENT_LOCATION_NOTE}
                    </h3>
                    <div>
                      <LocationCard
                        isLocationModal={isLocationModal}
                        setIsLocationModal={setIsLocationModal}
                        value={selectedValues.selectedLocation}
                        userLocation={userLocation}
                      />
                      <div ref={locationModalRef}>
                        {isLocationModal ? (
                          <Dropdown
                            values={locations}
                            setIsLocationModal={setIsLocationModal}
                            setSelectedValues={setSelectedValues}
                            locationData={{
                              prevLocation: selectedValues.selectedLocation,
                              userLocation,
                            }}
                            setUserLocation={setUserLocation}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
                {(error?.code === Messages.ERROR_CODES.INACTIVE_USER.code || error?.code === Messages.ERROR_CODES.INVALID_OFFICE_CATEGORY.code)   ? (
                  <InlineMessageBlock
                    title={error.title}
                    message={error.message}
                    subMessage={error.subMessage}
                  />
                ) : (
                  <>
                    <div className="flex items-center mb-4">
                      <h2 className="grow text-xl font-bold mr-4">
                        {Messages.SHOP.TITLE}
                      </h2>
                      <Search onSearchText={onSearchText} />
                    </div>
                    <ul>
                      {items && items.length > 0 ? (
                        items.map((item) => {
                          return (
                            <Item
                              data={item}
                              key={item.id}
                              updateQty={updateCart}
                            />
                          );
                        })
                      ) : loader ? null : (
                        <h2>{Messages.SHOP.NO_ITEMS}</h2>
                      )}
                    </ul>
                    <Loader show={loader} />
                    <CartHighlighter
                      count={cartCount}
                      cartTotalValue={cartTotalValue}
                    />
                    {error ? (
                      <InlineMessageBlock
                        title={error.title}
                        message={error.message}
                        subMessage={error.subMessage}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </section>
          </div>
          <Modal
            visible={showModal}
            data={{ userDetails, eventDetails }}
            onClose={handleOnClose}
            setSelectedValues={setSelectedValues}
            isEventTime={isEventTime}
          />
        </div>
      );
    };

export default Home;

